import { useTranslation } from 'react-i18next';
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyInfosManagement } from '../components/CompanyInfosManagement';
import { useGetCompanyById, useUpdateCompany } from '@/hooks/company.hooks';
import {
  companySchema,
  TCompanySchema,
} from '@/schemas/company/company.schema';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { EntityRelationshipsManagement } from '@/components/EntityRelationshipsManagement';
import { TBranchSchema } from '@/schemas/branch/branch.schema';
import {
  useGetAssignedBranchesToCompany,
  useGetUnassignedBranchesToCompany,
  useUpdateBranch,
} from '@/hooks/branch.hooks';
import { useGetTab } from '@/hooks/tab.hooks';
import { EditCompanyTab } from '@/type/ui/tab/companyTab';
import { CompanyWhiteLabelManagement } from '../components/CompanyWhiteLabelManagement';
import { getLanguage } from '@/utils/language.utils';

export const EditCompanyPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('company');
  const { data, isLoading } = useGetCompanyById(`${id}`);
  const navigate = useNavigate();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditCompanyTab.INFOS,
  });

  const {
    watch: watchCompany,
    control: companyControl,
    register: registerCompany,
    setValue: setCompanyValue,
    setError: setCompanyError,
    handleSubmit: handleSubmitCompany,
    formState: { errors: companyErrors, isSubmitting: isSubmittingCompany },
  } = useForm<TCompanySchema>({
    resolver: zodResolver(companySchema),
  });

  const { mutate: mutateUpdateCompany, isPending: isPendingUpdateCompany } =
    useUpdateCompany({
      t,
      handleSuccess: () => navigate('/companies'),
    });

  const onSubmitCompanyInfos = (company: TCompanySchema) => {
    const { languageCode } = getLanguage();

    const companyWithId = {
      id,
      ...company,
      languageCode,
    };
    mutateUpdateCompany(companyWithId);
  };

  return (
    <>
      <PageHeader
        backToLink={history?.length > 1 ? -1 : '/companies'}
        title={t('theCompany')}
        subTitle={data?.data?.name}
        subTitleIconClassName="ki-abstract-42"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text="Liste de sociétés" linkTo="/companies" />
            <BreadcrumbItem text={data?.data?.name} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('companyNotFound')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditCompanyTab.INFOS}
              onChange={() => navigateToTab(EditCompanyTab.INFOS)}
            />
            <TabItem
              text={t('linkedBranches')}
              leftIcon={<i className="ki-outline ki-note-2" />}
              active={currentTab === EditCompanyTab.LINKED_BRANCHES}
              onChange={() => navigateToTab(EditCompanyTab.LINKED_BRANCHES)}
            />
            {data?.data?.group?.isWhiteLabel && (
              <TabItem
                text={t('common:infosProPart')}
                leftIcon={<i className="ki-outline ki-copy-success" />}
                active={currentTab === EditCompanyTab.WHITE_LABEL}
                onChange={() => navigateToTab(EditCompanyTab.WHITE_LABEL)}
              />
            )}
          </TabList>
          {currentTab === EditCompanyTab.INFOS && (
            <div className="flex flex-col gap-12">
              <CompanyInfosManagement<TCompanySchema>
                t={t}
                data={!isLoading && data?.data}
                watch={watchCompany}
                register={registerCompany}
                setValue={setCompanyValue}
                setError={setCompanyError}
                errors={companyErrors}
                submitDisabled={isSubmittingCompany || isPendingUpdateCompany}
                onSubmit={handleSubmitCompany(onSubmitCompanyInfos)}
              />
            </div>
          )}
          {currentTab === EditCompanyTab.LINKED_BRANCHES && (
            <EntityRelationshipsManagement<TBranchSchema>
              t={t}
              foreignKeyId={id}
              foreignKeyField={'companyId'}
              title={t('branchesLinkedToThisCompany')}
              searchText={t('common:searchBranch')}
              mainContentNotFoundText={t('noBranchLinkedToThisCompany')}
              addButtonText={t('common:addBranch')}
              modalTitle={`${t('selectOneOrManyBranchesBelow')} :`}
              modalDataNotFoundText={t('noBranchAvailable')}
              useGetUnassignedEntities={useGetUnassignedBranchesToCompany}
              useGetAssignedEntities={useGetAssignedBranchesToCompany}
              useUpdateEntity={useUpdateBranch}
            />
          )}
          {currentTab === EditCompanyTab.WHITE_LABEL && (
            <CompanyWhiteLabelManagement<TBranchSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchCompany}
              control={companyControl}
              register={registerCompany}
              setValue={setCompanyValue}
              setError={setCompanyError}
              errors={companyErrors}
              submitDisabled={isSubmittingCompany || isPendingUpdateCompany}
              onSubmit={handleSubmitCompany(onSubmitCompanyInfos)}
            />
          )}
        </div>
      )}
      {(isLoading || isSubmittingCompany || isPendingUpdateCompany) && (
        <Loader />
      )}
    </>
  );
};
